/**
 * AppShell - Main application layout with sidebar, header, and content area.
 */

import { ReactNode, useEffect, useState } from 'react'
import { Sidebar } from './Sidebar'
import { Header } from './Header'
import { CommandMenu } from './CommandMenu'
import { useNavigationStore } from '@/stores/navigationStore'
import { useFilterInitialization } from '@/hooks/useFilters'
import { useNotificationStore } from '@/stores/notificationStore'
import { useBrandingStore } from '@/stores/brandingStore'
import { useAuthContext } from '@/hooks/AuthContext'
import { cn } from '@/lib/utils'
import { ScrollContainerContext } from '@/contexts/ScrollContainerContext'
import { AISidebar } from '@/components/ai/AISidebar'

interface AppShellProps {
  children: ReactNode
}

export function AppShell({ children }: AppShellProps) {
  const isSidebarCollapsed = useNavigationStore(
    (state) => state.isSidebarCollapsed
  )

  // Initialize filter config and values when study changes
  useFilterInitialization()

  // Auth context for getting token (needed for SSE which doesn't support headers)
  const { getToken } = useAuthContext()

  // Scroll container ref for IntersectionObserver-based lazy loading
  const [scrollContainer, setScrollContainer] = useState<HTMLElement | null>(null)

  // Initialize branding
  const { fetchBranding, isInitialized: brandingInitialized } = useBrandingStore()

  // Initialize notifications + SSE + branding ONCE on mount
  // Uses getState() to avoid dependency on store selectors which change reference
  useEffect(() => {
    const store = useNotificationStore.getState()

    // Fetch initial unread count
    store.fetchUnreadCount()

    // Connect SSE for real-time updates (with token for auth)
    getToken().then((token) => {
      if (token) {
        useNotificationStore.getState().connectSSE(token)
      }
    })

    // Load branding configuration
    if (!brandingInitialized) {
      fetchBranding()
    }

    return () => {
      useNotificationStore.getState().disconnectSSE()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="min-h-screen bg-background">
      {/* Global search (Ctrl+K) */}
      <CommandMenu />

      {/* Sidebar */}
      <Sidebar />

      {/* AI Sidebar (global floating panel) */}
      <AISidebar />

      {/* Main content area */}
      <div
        className={cn(
          'flex min-h-screen flex-col transition-all duration-300',
          isSidebarCollapsed ? 'lg:pl-16' : 'lg:pl-64'
        )}
      >
        {/* Header */}
        <Header />

        {/* Page content */}
        <main ref={setScrollContainer} className="flex-1 overflow-auto">
          <ScrollContainerContext.Provider value={scrollContainer}>
            <div className="px-6 py-6">{children}</div>
          </ScrollContainerContext.Provider>
        </main>
      </div>
    </div>
  )
}
